
















































































































































































































































import Vue from "vue";
import ApiSearchPage from "@/components/apiSearch/components/ApiSearchPage.vue";
import CheckBox from "@/components/CheckBox.vue";
import eventBus from "@/services/eventBus";
import auth from "@/services/auth";
import CurrentAccountPreAccFilterPanel from "../components/CurrentAccountPreAccFilterPanel.vue";
import CurrentAccountActionItemsGrid from "../components/CurrentAccountActionItemsGrid.vue";
import CurrentAccountAccDatePanel from "../components/CurrentAccountAccDatePanel.vue";
import CurrentAccountPreAccountingApi from "../services/CurrentAccountPreAccountingApi";
import {
  CurrentAccountAccDateResultRow,
  CurrentAccountAccItemSaveResult,
  CurrentAccountActionItem,
  CurrentAccountActionRef,
  CurrentAccountPreAccError,
  CurrentAccountPreAccLookups,
  CurrentAccountUpdatedStatus,
} from "../models/CurrentAccountPreAccounting";
import { t } from "@/plugins/i18n";

interface Data {
  lookups: CurrentAccountPreAccLookups | null;
  rowItems: Record<string, CurrentAccountActionItem[]>;
  rowErrors: Record<string, CurrentAccountPreAccError[]>;
  detailMode: Record<string, string>;
  checkedRefs: CurrentAccountActionRef[];
  statusActionRunning: boolean;
  recontRunning: boolean;
  recontConfirmIds: number[];
  /** A Kontírozás gomb alatti megerősítő doboz nyitva */
  reAccountConfirmOpen: boolean;
  lastItems: any[];
}

interface Methods {
  isSelectable(item: any): boolean;
  notSelectableReason(item: any): string;
  toggleChecked(item: any): void;
  refFromItem(item: any): CurrentAccountActionRef;
  refreshCheckedRefs(items: any[]): void;
  syncCheckedItems(items: any[]): void;
  toggleItems(item: any): Promise<void>;
  toggleErrors(item: any): Promise<void>;
  itemsFor(item: any): CurrentAccountActionItem[];
  errorsFor(item: any): CurrentAccountPreAccError[];
  canModify(item: any): boolean;
  setStatus(status: number): Promise<void>;
  confirmRecont(): void;
  startRecont(): void;
  runRecont(includeNotPostable: boolean): Promise<void>;
  checkPermission(name: string): boolean;
  applyStatusUpdates(updated: CurrentAccountUpdatedStatus[]): void;
  onAccItemSaved(result: CurrentAccountAccItemSaveResult): void;
  onAccDateSaved(rows: CurrentAccountAccDateResultRow[]): void;
  onFlagsSaved(item: any): void;
  reloadGrid(): void;
  invalidateRowCaches(): void;
  onFiltersReady(): void;
  onItemsLoaded(items: any[]): void;
}

interface Computed {
  columns: any[];
  errorColumns: any[];
}

/** A státusz-műveletek sikerüzenetei (K2D menüpont-feliratok szerint) */
const STATUS_MESSAGES: Record<number, string> = {
  3: t("CurrentAccountPreAccounting_AKijeloltFolyoszamlaMuveletekFeladhatoStatuszba"),
  6: t("CurrentAccountPreAccounting_AKijeloltFolyoszamlaMuveletekFeladasaLefutott"),
  33: t("CurrentAccountPreAccounting_AKijeloltFolyoszamlaMuveletekVisszaveveA"),
  9: t("CurrentAccountPreAccounting_AKijeloltFolyoszamlaMuveletekNemFeladando"),
};

export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    ApiSearchPage,
    CheckBox,
    CurrentAccountPreAccFilterPanel,
    CurrentAccountActionItemsGrid,
    CurrentAccountAccDatePanel,
  },

  data() {
    return {
      lookups: null,
      rowItems: {},
      rowErrors: {},
      detailMode: {},
      checkedRefs: [],
      statusActionRunning: false,
      recontRunning: false,
      recontConfirmIds: [],
      reAccountConfirmOpen: false,
      lastItems: [],
    };
  },

  async beforeMount() {
    try {
      this.lookups = await CurrentAccountPreAccountingApi.getLookups();
    } catch {
      // A globális hibakezelő már megjelenítette a backend üzenetét; a lista lookup nélkül
      // is betölt (a státusz-lista és a Felad oszlop marad üres). A lookups propnak ETTŐL
      // FÜGGETLENÜL is meg kell változnia (null -> valami) — a szűrőpanel (ld.
      // CurrentAccountPreAccFilterPanel.vue) erre a változásra várva számolja ki az
      // alapértelmezett kontír státusz pipákat; enélkül a panel örökre "ready=false" maradna.
      this.lookups = { accStatuses: [], isUpChangeable: false };
      eventBus.$emit("error", t("CurrentAccountPreAccounting_AFolyoszamlaKontirozasTorzsadataiStatuszokNem"));
    }
  },

  methods: {
    // K2D dw_head::clicked kapui: 2/3 (bank/pénztár kiegyenlítés) és a nem kontírozható
    // típus (is_can_acc_cont <> '+') nem jelölhető ki. A K2D itt csak a 2/3-at nevesíti;
    // a 18/20 típust a "Csak folyószámla rendezés" szűrő rekeszti ki, kikapcsolt szűrőnél
    // a kontírozó (f_action_cont_os) úgyis 0-val (nem kontírozható) tér vissza rájuk.
    isSelectable(item: any): boolean {
      if (item.type === 2 || item.type === 3) return false;
      return item.isCanAccCont === true;
    },

    notSelectableReason(item: any): string {
      if (item.type === 2 || item.type === 3) return t("CurrentAccountPreAccounting_IlyenTipusuAkciotNemIttKell");
      return t("CurrentAccountPreAccounting_AzIlyenTipusuFolyoszamlaMuveleteKe");
    },

    refFromItem(item: any): CurrentAccountActionRef {
      return {
        id: item.id,
        type: item.type,
        typeName: item.typeName,
        accStatus: item.accStatus,
        accDate: item.accDate,
      };
    },

    // A §-lista (K2D sel_c) lapozáson át is megmarad — ebből futnak majd a csoportos műveletek
    toggleChecked(item: any) {
      if (!this.isSelectable(item)) return;
      item.selectedStatus = !item.selectedStatus;
      const idx = this.checkedRefs.findIndex((x) => x.id === item.id);
      if (item.selectedStatus && idx < 0) {
        this.checkedRefs.push(this.refFromItem(item));
      } else if (!item.selectedStatus && idx >= 0) {
        this.checkedRefs.splice(idx, 1);
      }
    },

    // Az ApiSearchPage @items eseménye a friss találat-listát adja — ebből frissülnek a
    // kijelölt sorok tárolt adatai, a lenyíló tétel-cache elavul, és visszaállnak a pipák.
    onItemsLoaded(items: any[]): void {
      this.refreshCheckedRefs(items);
      this.lastItems = items;
      this.invalidateRowCaches();
      this.syncCheckedItems(items);
    },

    // Lapozás/újratöltés után a pipák visszaállítása
    syncCheckedItems(items: any[]): void {
      (items ?? []).forEach((item) => {
        const ref = this.checkedRefs.find((x) => x.id === item.id);
        if (ref && !item.selectedStatus) item.selectedStatus = true;
      });
    },

    // A kijelölt sorok tárolt adatai (státusz, könyv. dátum) frissítése a friss találatból —
    // eseménykezelőből, renderen kívül
    refreshCheckedRefs(items: any[]): void {
      (items ?? []).forEach((item) => {
        const idx = this.checkedRefs.findIndex((x) => x.id === item.id);
        if (idx >= 0) this.checkedRefs.splice(idx, 1, this.refFromItem(item));
      });
    },

    // A művelet tételeinek (és kontír sorainak) lenyitása/zárása — egy hívás, tételenként
    // cache-elve; újratöltés után a cache elavul (invalidateRowCaches). Ugyanaz a lenyíló
    // sor szolgálja a hibalistát is, ezért a mód is vált (K2D: két külön ablak).
    async toggleItems(item: any) {
      const key = `${item.id}`;
      if (item.selected && this.detailMode[key] !== "errors") {
        item.selected = false;
        return;
      }
      // A gomb betöltés közben is változatlan marad (nincs spinner) — user-kérés
      if (!this.rowItems[key]) {
        try {
          const items = await CurrentAccountPreAccountingApi.getItems(item.id);
          Vue.set(this.rowItems, key, items);
        } catch {
          // A backend üzenetét a globális hibakezelő már megjelenítette
          return;
        }
      }
      Vue.set(this.detailMode, key, "items");
      item.selected = true;
    },

    // K2D pb_err: a művelet kontírozási hibalistája ugyanabban a lenyíló sorban
    async toggleErrors(item: any) {
      const key = `${item.id}`;
      if (item.selected && this.detailMode[key] === "errors") {
        item.selected = false;
        return;
      }
      if (!this.rowErrors[key]) {
        try {
          const errors = await CurrentAccountPreAccountingApi.getErrors(item.id);
          Vue.set(this.rowErrors, key, errors);
        } catch {
          // A backend üzenetét a globális hibakezelő már megjelenítette
          return;
        }
      }
      Vue.set(this.detailMode, key, "errors");
      item.selected = true;
    },

    itemsFor(item: any): CurrentAccountActionItem[] {
      return this.rowItems[`${item.id}`] ?? [];
    },

    errorsFor(item: any): CurrentAccountPreAccError[] {
      return this.rowErrors[`${item.id}`] ?? [];
    },

    // K2D ue_modify: 3 (Feladható), 4 (Feladva) és 6 (Fők. rendsz. feladva) státuszon
    // már nem lehet a kontírozáson változtatni
    canModify(item: any): boolean {
      return ![3, 4, 6].includes(item.accStatus);
    },

    checkPermission(name: string): boolean {
      const menuPerm = auth.menuPermissions.find((x: any) => x.name == name);
      return menuPerm?.isGranted ?? false;
    },

    // A megerősítő doboz Megerősítés gombja: bezárja a dobozt és indítja a kontírozást
    confirmRecont() {
      this.reAccountConfirmOpen = false;
      this.startRecont();
    },

    // K2D pb_cont: ha a kijelöltek közt "Nem feladandó" (9) státuszú is van, a K2D
    // műveletenként rákérdez — itt egyszer, egy beágyazott sávban; különben indul.
    startRecont() {
      if (this.checkedRefs.length === 0 || this.recontRunning) return;
      const notPostable = this.checkedRefs.filter((x) => x.accStatus === 9).map((x) => x.id);
      if (notPostable.length > 0) {
        this.recontConfirmIds = notPostable;
        return;
      }
      this.runRecont(false);
    },

    // A K2D pb_cont az első hibánál megáll; a szerver ugyanígy, és a kimaradt műveleteket
    // is megnevezi. A sikeres sorok státusza a listában frissül.
    async runRecont(includeNotPostable: boolean) {
      const ids = includeNotPostable
        ? this.checkedRefs.map((x) => x.id)
        : this.checkedRefs.filter((x) => x.accStatus !== 9).map((x) => x.id);
      this.recontConfirmIds = [];
      if (ids.length === 0) {
        eventBus.$emit("error", t("CurrentAccountPreAccounting_NemMaradtKontirozandoMuveletAKijelolesben"));
        return;
      }

      this.recontRunning = true;
      try {
        const result = await CurrentAccountPreAccountingApi.recont(ids, includeNotPostable);
        this.applyStatusUpdates(result.updated);

        if (result.failures.length > 0) {
          const lines = [t("CurrentAccountPreAccounting_AKontirozasNemFutottLeMinden")];
          result.failures.forEach((f) => lines.push(f));
          eventBus.$emit("error", lines.join("\n"));
        } else {
          eventBus.$emit("success", t("CurrentAccountPreAccounting_AKijeloltFolyoszamlaMuveletekKontirozasaLefutott"));
        }
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.recontRunning = false;
        this.reloadGrid();
      }
    },

    // K2D ue_menu_acc_status: csoportos státusz állítás a kijelölt műveleteken.
    // A hibák műveletenként gyűlnek, a K2D is egy összegző üzenetben mutatja őket.
    async setStatus(status: number) {
      if (this.checkedRefs.length === 0 || this.statusActionRunning) return;
      this.statusActionRunning = true;
      try {
        // TODO: a CURRENTACCOUNTDIRECTPOST jogosultsági kulcs törzsbe vétele után a jog
        // ténylegesen kiosztható — addig a K2D alapesete él (5-ös műveletet nem ad fel
        // egy lépésben). A kulcs a K2D rejtett cbx_egybol_feladva kapcsolójának felel meg.
        const allowDirectPost = this.checkPermission("CURRENTACCOUNTDIRECTPOST");
        const result = await CurrentAccountPreAccountingApi.setStatuses(
          status,
          this.checkedRefs.map((x) => x.id),
          allowDirectPost
        );

        // A sikeres sorok státusza a listában is frissül, és lekerülnek a kijelölésről
        this.applyStatusUpdates(result.updated);

        if (result.failures.length > 0) {
          const lines = [t("CurrentAccountPreAccounting_AKovetkezoAkciokStatuszallitasaNemSikerult")];
          result.failures.forEach((f) => lines.push(f));
          eventBus.$emit("error", lines.join("\n"));
        } else {
          eventBus.$emit("success", STATUS_MESSAGES[status] ?? t("CurrentAccountPreAccounting_AStatuszAtallitva"));
        }
      } catch {
        // A backend üzenetét a globális hibakezelő már megjelenítette
      } finally {
        this.statusActionRunning = false;
        this.reloadGrid();
      }
    },

    // A sikeresen átállított műveletek új státusza a gridbe, és le a kijelölésről
    applyStatusUpdates(updated: CurrentAccountUpdatedStatus[]) {
      (updated ?? []).forEach((u) => {
        const row = (this.lastItems ?? []).find((x: any) => x.id === u.actionId);
        if (row) {
          row.accStatus = u.accStatus;
          row.accStatusName = u.accStatusName;
          row.selectedStatus = false;
        }
        const idx = this.checkedRefs.findIndex((x) => x.id === u.actionId);
        if (idx >= 0) this.checkedRefs.splice(idx, 1);
      });
    },

    // Az inline mentés után a művelet kontír státusza változhatott (5 vagy 8). A kontír sor
    // mezőit a grid helyben frissítette, ezért a lista nem töltődik újra (a lenyitott sorok
    // nyitva maradnak) — csak a fej státusza és az elavult hibalista-cache frissül
    onAccItemSaved(result: CurrentAccountAccItemSaveResult) {
      if (!result) return;
      const row = (this.lastItems ?? []).find((x: any) => x.id === result.actionId);
      if (row) {
        row.accStatus = result.accStatus;
        row.accStatusName = result.accStatusName;
        row.hasError = result.accStatus === 8;
      }
      Vue.delete(this.rowErrors, `${result.actionId}`);
    },

    // A dátummódosítás sikeres sorainál a fej dátuma is frissül
    onAccDateSaved(rows: CurrentAccountAccDateResultRow[]) {
      (rows ?? []).filter((r) => r.success).forEach((r) => {
        const row = (this.lastItems ?? []).find((x: any) => x.id === r.actionId);
        if (row) row.accDate = r.accDate;
      });
      this.reloadGrid();
    },

    // A Felad jelölők mentése után a tétel-cache elavul (a kontírsorok is_up mezője változott)
    onFlagsSaved(item: any) {
      Vue.delete(this.rowItems, `${item.id}`);
      if (item) item.selected = false;
    },

    reloadGrid() {
      this.invalidateRowCaches();
      eventBus.$emit("current-account-pre-acc-actions:reload");
    },

    invalidateRowCaches() {
      Object.keys(this.rowItems).forEach((key) => Vue.delete(this.rowItems, key));
      Object.keys(this.rowErrors).forEach((key) => Vue.delete(this.rowErrors, key));
    },

    // Az ApiSearchPage a saját beforeMount-jában kérdezne le, még a szűrőpanel létrejötte
    // előtt — tehát a K2D alapértékek nélkül (státusz-szűrés nélküli lista). Ezért az
    // automatikus első lekérdezés ki van kapcsolva, és innen indítjuk, amikor a panel már
    // beírta az alapállapotot. A $nextTick azért kell, mert az ApiSearchPage a szűrő-
    // watcher elnyelő flagjeit is a következő tick-ben ejti el.
    onFiltersReady() {
      this.$nextTick(() => eventBus.$emit("current-account-pre-acc-actions:reload"));
    },
  },

  computed: {
    // Az ApiSearchPage ebből építi a szerver felé menő filter/sort modellt és a Kendo grid
    // oszlopait — a K2D d_g_action oszlopsorrendje
    columns: () => [
      { cell: "commandTemplate", filterable: false, sortable: false, width: 100 },
      // A művelet azonosítója balra igazítva (a többi szám jobbra marad) — így a
      // szemnek van hova kapaszkodnia a lista bal szélén
      { field: "id", title: t("Azonosito"), width: 110 },
      { field: "typeName", title: t("Tipus"), width: 240 },
      { field: "userName", title: t("Felhasznalo"), width: 180 },
      { field: "appl", title: t("Alkalmazas"), width: 130 },
      { field: "note", title: t("Megjegyzes"), cell: "noteTemplate" },
      { field: "insDate", title: t("RogzitesDatuma"), width: 140, fieldClass: "right_text" },
      { field: "accDate", title: t("KonyvelesDatuma"), width: 130, fieldClass: "right_text" },
      { field: "accStatusName", title: t("KonyvelesAllapot"), cell: "accStatusTemplate", width: 200 },
    ],

    // A lenyitott hibalista oszlopai — width nélkül, a table-layout: fixed osztja el őket
    errorColumns: () => [
      { field: "itemId", title: t("Tetel") },
      { field: "errTypeName", title: t("HibaTipusa") },
      { field: "note", title: t("Megjegyzes"), cell: "errorNoteTemplate" },
    ],
  },
});
